
import React, { useState, useEffect } from 'react';
import {
  Zap, ShieldCheck, Users, BrainCircuit, HeartHandshake,
  ArrowRight, CheckCircle2, Layout, Sparkles, MessageSquare,
  ChevronRight, ChevronDown, Globe, BarChart3, Target, Briefcase, Building2,
  Mail, Info, Phone, Play, Star, MapPin, Send, Loader2,
  FileText, Settings, HelpCircle, Quote, Plus, XCircle, TrendingUp, Lock
} from 'lucide-react';
import WerftoolsLogo from './WerftoolsLogo';
import { WebsiteConfig, WebsiteSection, Vacancy } from '../types';

// Local Dutch Plans for Landing Page Display
const DUTCH_PLANS = [
  {
    id: 'solo',
    name: 'Single Vacancy',
    price: '€1.250 /vacature',
    features: ['AI Marktonderzoek & Persona', 'Outreach 1:1 Automatisering', 'Basis Kanban Bord', 'Toegang tot 1 Recruiter']
  },
  {
    id: 'hybrid',
    name: 'Hybrid Growth',
    price: '€2.499 /mnd',
    features: ['Onbeperkte AI Tools', 'Werkplaats Recruiter Support', 'Team Management & Rollen', 'ATS Integratie']
  },
  {
    id: 'scale',
    name: 'Enterprise Flow',
    price: 'Op aanvraag',
    features: ['Volledige Automatisering', 'Maatwerk Integraties', 'Dedicated Account Manager', 'SLA & Priority Support']
  }
];

interface LandingPageProps {
  onLoginClick: () => void;
  onAdminClick?: () => void;
  config?: WebsiteConfig;
  isEditing?: boolean;
  selectedSectionId?: string | null;
  onSectionClick?: (id: string) => void;
  vacancies?: Vacancy[];
}

type ViewType = 'home' | 'agencies' | 'companies' | 'employees' | 'about' | 'pricing' | 'contact' | 'careers';

const LandingPage: React.FC<LandingPageProps> = ({
  onLoginClick,
  onAdminClick,
  config,
  isEditing = false,
  selectedSectionId,
  onSectionClick,
  vacancies = []
}) => {
  const [activeView, setActiveView] = useState<ViewType>('home');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeView]);

  const NavLink = ({ view, label }: { view: ViewType, label: string }) => (
    <button
      onClick={() => setActiveView(view)}
      className={`text-[11px] font-black uppercase tracking-widest transition-all hover:-translate-y-0.5 ${activeView === view ? 'text-[#e51b23]' : 'text-slate-500 hover:text-slate-900'}`}
    >
      {label}
    </button>
  );

  // If no config pages are found or active page has no sections, use static content
  const currentSections = config?.pages?.[activeView] || [];
  const shouldUseStatic = currentSections.length === 0;

  return (
    <div className={`bg-white font-sans text-slate-900 selection:bg-red-100 selection:text-red-900 min-h-screen flex flex-col ${isEditing ? 'cursor-default' : ''}`}>
      {/* HEADER */}
      <header className={`fixed top-0 left-0 w-full z-[100] bg-white/80 backdrop-blur-xl border-b border-slate-100 transition-all duration-300 ${isEditing ? 'pr-[420px]' : ''}`}>
        <div className="max-w-[1600px] mx-auto px-6 h-24 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer group" onClick={() => setActiveView('home')}>
            <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
              <WerftoolsLogo className="w-5 h-5" color="white" />
            </div>
            <span className="text-xl font-black tracking-tight text-slate-900">Werf<span className="text-[#e51b23]">tools</span></span>
          </div>

          <nav className="hidden xl:flex items-center gap-8 bg-slate-50/50 px-8 py-3 rounded-2xl border border-slate-100">
            <NavLink view="home" label="Home" />
            <NavLink view="careers" label="Vacatures" />
            <NavLink view="agencies" label="Bureaus" />
            <NavLink view="companies" label="Bedrijven" />
            <NavLink view="employees" label="Talent" />
            <NavLink view="pricing" label="Prijzen" />
          </nav>

          <div className="flex items-center gap-4">
            <button onClick={onLoginClick} className="text-[11px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-900 transition-colors hidden sm:block">Inloggen</button>
            <button onClick={onLoginClick} className="bg-[#0F172A] text-white px-6 py-3 rounded-xl font-black text-[11px] uppercase tracking-widest hover:bg-[#e51b23] transition-all shadow-lg shadow-slate-900/10 active:scale-95 flex items-center gap-2">
              Start Nu <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </header>

      <main className={`flex-1 pt-24 transition-all ${isEditing ? 'mr-[420px]' : ''}`}>
        {!shouldUseStatic ? (
          // DYNAMIC CONTENT
          currentSections.filter(s => s.isVisible).map(section => {
            const SectionComp = DYNAMIC_COMPONENTS[section.type] || (() => null);
            return (
              <div
                key={section.id}
                onClick={() => isEditing && onSectionClick?.(section.id)}
                className={`relative group transition-all ${isEditing ? 'cursor-pointer hover:opacity-90' : ''}`}
              >
                {isEditing && (
                  <div className={`absolute inset-0 z-50 pointer-events-none transition-all border-4 rounded-[2rem] m-4 ${selectedSectionId === section.id ? 'border-red-500 bg-red-500/5' : 'border-transparent group-hover:border-red-500/30 group-hover:bg-red-500/5'}`}>
                    <div className="absolute top-4 left-4 bg-red-600 text-white text-[9px] font-black uppercase px-3 py-1.5 rounded-lg shadow-lg flex items-center gap-2">
                      <Settings size={12} /> {section.type} Section
                    </div>
                  </div>
                )}
                <SectionComp section={section} onLoginClick={onLoginClick} />
              </div>
            );
          })
        ) : (
          // STATIC FALLBACK CONTENT (Sales Optimized - Dutch)
          <>
            {activeView === 'home' && (
              <>
                <HeroSection section={{
                  id: 's-hero', type: 'hero',
                  title: 'Hybride Recruitment. De Snelheid van AI, de Kwaliteit van Mensen.',
                  subtitle: 'Werftools combineert autonome AI-agenten met menselijke expertise voor een ongeëvenaarde wervingsmotor. Laat technologie het zware werk doen, zodat jij kunt focussen op de kandidaat.',
                  isVisible: true,
                  content: { ctaLabel: 'Start Slimmer Werven', imageUrl: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&q=80&w=1600' }
                }} onLoginClick={onLoginClick} />

                <LogosSection section={{
                  id: 's-logos', type: 'logos', title: 'Vertrouwd door 500+ Innovators', isVisible: true,
                  content: { logos: ['https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg', 'https://upload.wikimedia.org/wikipedia/commons/5/51/IBM_logo.svg', 'https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg', 'https://upload.wikimedia.org/wikipedia/commons/0/08/Netflix_2015_logo.svg'] }
                }} />

                <FomoSection />

                <FeatureSection section={{
                  id: 's-feat', type: 'features', title: 'Het Hybride Voordeel', isVisible: true,
                  content: {
                    features: [
                      { title: 'AI Sourcing Engine', desc: 'Onze agenten autonoom scannen het web om verborgen talent te vinden en te benaderen.' },
                      { title: 'Menselijke Validatie', desc: 'Elke AI-match wordt dubbel gecheckt door een senior recruiter voordat jij deze ziet. Kwaliteit boven kwantiteit.' },
                      { title: 'Directe Pijplijnen', desc: 'Lanceer een vacature en ontvang binnen 48 uur een gekwalificeerde shortlist. Geen wachttijd.' }
                    ]
                  }
                }} />

                <TextImageSection section={{
                  id: 's-txt', type: 'text_image', title: 'Datagedreven Beslissingen', subtitle: 'Stop met werven op onderbuikgevoel. Krijg realtime marktinzichten, salarisbenchmarks en conversie-analyses.',
                  isVisible: true, styles: { imagePosition: 'right' },
                  content: { bulletPoints: ['Live Dashboards', 'Conversie Optimalisatie', 'Budget Intelligentie'], imageUrl: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=1200' }
                }} />
              </>
            )}

            {/* Other views remain similar but with updated styling wrappers if needed */}
            {/* VACANCIES SECTION */}
            {activeView === 'careers' && (
              <section className="py-32 bg-slate-50 min-h-screen">
                <div className="max-w-[1200px] mx-auto px-6">
                  <div className="text-center max-w-2xl mx-auto mb-20">
                    <span className="text-[#e51b23] font-black text-xs uppercase tracking-widest mb-4 block">Carrièrekansen</span>
                    <h2 className="text-5xl font-black text-slate-900 tracking-tight mb-6">Ontdek Onze Vacatures</h2>
                    <p className="text-slate-500 font-medium text-lg">Vind de baan die bij je past en solliciteer direct met de snelheid van AI.</p>
                  </div>

                  {vacancies.filter(v => v.data?.publishedToSite).length === 0 ? (
                    <div className="text-center py-20 bg-white rounded-[2.5rem] border border-slate-200 shadow-sm">
                      <p className="text-slate-400 font-bold uppercase tracking-widest text-sm">Op dit moment zijn er geen openstaande vacatures.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-6">
                      {vacancies.filter(v => v.data?.publishedToSite).map((vacancy) => (
                        <div key={vacancy.id} className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all group flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                          {/* Client Logo if available */}
                          {vacancy.client?.logo_url && (
                            <div className="w-16 h-16 md:w-20 md:h-20 shrink-0 bg-white border border-slate-100 rounded-2xl flex items-center justify-center p-2 shadow-sm">
                              <img src={vacancy.client.logo_url} alt={vacancy.client.name} className="w-full h-full object-contain" />
                            </div>
                          )}
                          <div className="flex-1">
                            <h3 className="text-2xl font-black text-slate-900 tracking-tight mb-2 group-hover:text-[#e51b23] transition-colors">{vacancy.title}</h3>
                            <div className="flex flex-wrap gap-4 text-xs font-bold text-slate-500 uppercase tracking-wide">
                              <span className="flex items-center gap-1"><MapPin size={12} /> {vacancy.client?.address_city || 'Locatie Flexibel'}</span>
                              <span className="flex items-center gap-1"><Briefcase size={12} /> {vacancy.data?.category || 'General'}</span>
                              <span className="flex items-center gap-1"><Building2 size={12} /> {vacancy.client?.name || 'Onbekend'}</span>
                            </div>
                            <p className="mt-4 text-slate-500 line-clamp-2 max-w-2xl">{vacancy.description || vacancy.data?.marketResearchSummary || 'Bekijk de volledige omschrijving voor meer details.'}</p>
                          </div>
                          <button
                            onClick={() => window.open(`/vacancies/${vacancy.id}`, '_blank')}
                            className="px-8 py-4 bg-slate-900 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-[#e51b23] transition-all shadow-lg active:scale-95 whitespace-nowrap"
                          >
                            Bekijk Vacature
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            )}

            {activeView === 'pricing' && (
              <section className="py-32 bg-slate-50">
                <div className="max-w-[1200px] mx-auto px-6">
                  <div className="text-center max-w-2xl mx-auto mb-20">
                    <span className="text-[#e51b23] font-black text-xs uppercase tracking-widest mb-4 block">Prijsmodellen</span>
                    <h2 className="text-5xl font-black text-slate-900 tracking-tight mb-6">Eenvoudige, Transparante Prijzen.</h2>
                    <p className="text-slate-500 font-medium text-lg">Geen verborgen kosten. Geen langlopende contracten. Gewoon resultaat.</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {DUTCH_PLANS.map((plan) => (
                      <div key={plan.id} className="bg-white p-10 rounded-[2.5rem] border border-slate-200 shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all flex flex-col relative overflow-hidden group">
                        {plan.id === 'hybrid' && (
                          <div className="absolute top-0 inset-x-0 h-2 bg-gradient-to-r from-[#e51b23] to-orange-500"></div>
                        )}
                        <h3 className="text-xl font-black uppercase tracking-tight mb-2">{plan.name}</h3>
                        <p className="text-4xl font-black text-slate-900 mb-8 tracking-tight">{plan.price}</p>
                        <ul className="space-y-5 mb-10 flex-1">
                          {plan.features.map((f, i) => (
                            <li key={i} className="flex items-center gap-4 text-sm font-bold text-slate-600">
                              <div className="w-6 h-6 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                                <CheckCircle2 size={14} />
                              </div>
                              {f}
                            </li>
                          ))}
                        </ul>
                        <button onClick={onLoginClick} className={`w-full py-5 rounded-2xl font-black text-xs uppercase tracking-widest transition-all ${plan.id === 'hybrid' ? 'bg-[#0F172A] text-white hover:bg-[#e51b23] shadow-lg' : 'bg-slate-100 text-slate-900 hover:bg-slate-200'}`}>
                          Kies {plan.name}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Fallback for other pages */}
            {['agencies', 'companies', 'employees', 'contact'].includes(activeView) && (
              <div className="py-40 text-center bg-slate-50 m-6 rounded-[3rem] border border-slate-200">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm border border-slate-100">
                  <Settings size={32} className="text-slate-300" />
                </div>
                <h2 className="text-3xl font-black text-slate-900 uppercase tracking-tight">Binnenkort Beschikbaar</h2>
                <p className="text-slate-500 mt-3 font-medium">Aan deze pagina wordt nog gewerkt door ons creatieve team.</p>
              </div>
            )}
          </>
        )}
      </main>

      {/* Render the Footer from configuration if present, otherwise render default fallback */}
      {config?.pages?.home?.find(s => s.type === 'footer' && s.isVisible) ? null : (
        <footer className={`py-24 bg-[#0F172A] text-white transition-all ${isEditing ? 'mr-[420px]' : ''}`}>
          <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="text-center md:text-left">
              <div className="flex items-center gap-3 mb-4 justify-center md:justify-start">
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center">
                  <WerftoolsLogo className="w-5 h-5" color="#0F172A" />
                </div>
                <span className="text-2xl font-black tracking-tight">Werf<span className="text-[#e51b23]">tools</span></span>
              </div>
              <p className="text-slate-400 font-medium text-xs uppercase tracking-widest max-w-sm">
                Het Besturingssysteem voor Modern Recruitment. <br />De snelheid van AI gecombineerd met menselijke kwaliteit.
              </p>
            </div>

            <div className="flex gap-8">
              <button onClick={onAdminClick} className="text-[10px] font-bold text-slate-500 uppercase tracking-widest hover:text-white transition-colors flex items-center gap-2">
                <Lock size={12} /> Admin Login
              </button>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

// --- DYNAMIC SECTION COMPONENTS (Optimized for Sales) ---

const HeroSection: React.FC<{ section: WebsiteSection, onLoginClick: () => void }> = ({ section, onLoginClick }) => (
  <section className="relative py-32 lg:py-48 overflow-hidden bg-[#0F172A]">
    {/* Background Effects */}
    <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#e51b23]/20 to-transparent opacity-50 blur-3xl"></div>
    <div className="absolute bottom-0 left-0 w-1/3 h-full bg-indigo-900/20 blur-3xl"></div>

    <div className="max-w-[1600px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-20 items-center relative z-10">
      <div className="space-y-10 animate-in slide-in-from-left-4 duration-700">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm">
          <span className="w-2 h-2 rounded-full bg-[#e51b23] animate-pulse"></span>
          <span className="text-[10px] font-black text-white uppercase tracking-widest">Nieuw: AI Agent Froukje 2.0</span>
        </div>

        <h1 className="text-6xl lg:text-8xl font-black text-white leading-[0.9] tracking-tighter uppercase">
          {section.title}
        </h1>

        <p className="text-xl font-medium text-slate-400 max-w-xl leading-relaxed">
          {section.subtitle}
        </p>

        <div className="flex flex-wrap gap-4 pt-4">
          <button onClick={onLoginClick} className="bg-[#e51b23] text-white px-10 py-5 rounded-2xl font-black text-sm uppercase tracking-widest shadow-xl shadow-red-900/20 hover:bg-white hover:text-[#e51b23] transition-all hover:scale-105 active:scale-95">
            {section.content?.ctaLabel || 'Start Gratis Trial'}
          </button>
          <button className="px-10 py-5 rounded-2xl font-black text-sm uppercase tracking-widest text-white border border-white/20 hover:bg-white/10 transition-all flex items-center gap-3">
            <Play size={16} fill="currentColor" /> Bekijk Demo
          </button>
        </div>
      </div>

      {section.content?.imageUrl && (
        <div className="relative animate-in zoom-in-95 duration-1000 hidden lg:block">
          <div className="absolute inset-0 bg-gradient-to-tr from-[#e51b23] to-orange-500 rounded-[2.5rem] rotate-6 opacity-20 blur-xl"></div>
          <div className="relative rounded-[2.5rem] overflow-hidden shadow-2xl border-4 border-white/10 rotate-3 transition-transform hover:rotate-0 duration-500">
            <img src={section.content.imageUrl} className="w-full h-auto object-cover" alt="Hero" />

            {/* Floating Badges */}
            <div className="absolute top-10 left-10 bg-white p-4 rounded-2xl shadow-xl flex items-center gap-4 animate-bounce duration-[3000ms]">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-600"><CheckCircle2 size={20} /></div>
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Tijd Bespaard</p>
                <p className="text-lg font-black text-slate-900">18u / week</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  </section>
);

const FomoSection = () => (
  <section className="py-24 bg-white border-b border-slate-100">
    <div className="max-w-7xl mx-auto px-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
        <div>
          <h2 className="text-4xl font-black text-slate-900 uppercase tracking-tight mb-6 leading-none">Bereik talent <span className="text-[#e51b23]">sneller en slimmer</span></h2>
          <p className="text-lg text-slate-500 font-medium leading-relaxed mb-8">
            In de huidige markt is snelheid cruciaal. Werftools dicht het gat met passieve kandidaten door handmatige stappen te automatiseren, zodat jij sneller aan tafel zit met de juiste mensen.
          </p>
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 bg-red-50 rounded-2xl border border-red-100">
              <XCircle className="text-red-500 shrink-0" size={24} />
              <span className="font-bold text-slate-700 text-sm">Oude Manier: Post & Pray, handmatige screening, ghosting.</span>
            </div>
            <div className="flex items-center gap-4 p-4 bg-emerald-50 rounded-2xl border border-emerald-100 shadow-sm">
              <CheckCircle2 className="text-emerald-500 shrink-0" size={24} />
              <span className="font-bold text-slate-900 text-sm">Werftools: Directe AI sourcing, auto-scheduling, betrokken kandidaten.</span>
            </div>
          </div>
        </div>
        <div className="bg-slate-900 rounded-[2.5rem] p-10 text-white relative overflow-hidden flex flex-col justify-center">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#e51b23] rounded-full blur-[100px] opacity-20"></div>
          <div className="relative z-10">
            <div className="text-6xl font-black mb-2">300%</div>
            <p className="text-xl font-bold text-slate-300 mb-8">Meer Gekwalificeerde Leads</p>
            <div className="h-2 w-full bg-slate-800 rounded-full overflow-hidden">
              <div className="h-full w-3/4 bg-[#e51b23]"></div>
            </div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 mt-4">Gemiddelde prestatie vs traditionele bureaus</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const FeatureSection: React.FC<{ section: WebsiteSection }> = ({ section }) => (
  <section className="py-32 bg-slate-50" style={{ backgroundColor: section.styles?.backgroundColor }}>
    <div className="max-w-[1600px] mx-auto px-6">
      <div className="text-center mb-20">
        <span className="text-[#e51b23] font-black text-xs uppercase tracking-widest mb-4 block">Mogelijkheden</span>
        <h2 className="text-5xl font-black text-slate-900 uppercase tracking-tight" style={{ color: section.styles?.textColor }}>{section.title}</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {section.content?.features?.map((f: any, i: number) => (
          <div key={i} className="bg-white p-10 rounded-[2.5rem] shadow-sm border border-slate-200 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group">
            <div className="w-16 h-16 bg-slate-50 text-slate-900 rounded-2xl flex items-center justify-center mb-8 group-hover:bg-[#e51b23] group-hover:text-white transition-colors duration-300 shadow-inner">
              <Zap size={28} />
            </div>
            <h4 className="font-black text-xl text-slate-900 uppercase tracking-tight mb-4">{f.title}</h4>
            <p className="text-slate-500 font-medium leading-relaxed">{f.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const LogosSection: React.FC<{ section: WebsiteSection }> = ({ section }) => (
  <section className="py-16 border-b border-slate-100 bg-white">
    <div className="max-w-7xl mx-auto px-6">
      <p className="text-center text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-10">{section.title || "Industry Leaders"}</p>
      <div className="flex flex-wrap justify-center items-center gap-16 grayscale opacity-40 hover:opacity-100 transition-opacity duration-500">
        {section.content?.logos?.map((logo: string, i: number) => (
          <img key={i} src={logo} className="h-8 w-auto object-contain" alt="Partner" />
        ))}
      </div>
    </div>
  </section>
);

const TestimonialsSection: React.FC<{ section: WebsiteSection }> = ({ section }) => (
  <section className="py-32 bg-[#0F172A] text-white overflow-hidden">
    <div className="max-w-7xl mx-auto px-6">
      <h2 className="text-4xl font-black uppercase tracking-tight mb-20 text-center">{section.title}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {section.content?.items?.map((item: any, i: number) => (
          <div key={i} className="bg-white/5 border border-white/10 p-10 rounded-[2.5rem] backdrop-blur-md hover:bg-white/10 transition-colors">
            <Quote className="text-[#e51b23] mb-8" size={32} />
            <p className="text-xl font-medium text-slate-300 mb-10 leading-relaxed">"{item.quote}"</p>
            <div className="flex items-center gap-5">
              <div className="w-14 h-14 rounded-full bg-slate-800 overflow-hidden border-2 border-white/20">
                <img src={item.avatar} className="w-full h-full object-cover" />
              </div>
              <div>
                <p className="font-black uppercase tracking-tight text-sm">{item.name}</p>
                <p className="text-[10px] text-[#e51b23] font-bold uppercase tracking-widest">{item.role}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const FAQSection: React.FC<{ section: WebsiteSection }> = ({ section }) => (
  <section className="py-24 bg-white">
    <div className="max-w-3xl mx-auto px-6">
      <h2 className="text-3xl font-black uppercase tracking-tight mb-16 text-center">{section.title}</h2>
      <div className="space-y-4">
        {section.content?.items?.map((item: any, i: number) => (
          <div key={i} className="border border-slate-200 rounded-2xl overflow-hidden hover:border-slate-300 transition-colors">
            <button className="w-full p-6 text-left font-bold flex justify-between items-center hover:bg-slate-50 transition-colors">
              <span className="text-sm uppercase tracking-tight">{item.question}</span>
              <ChevronDown size={16} className="text-slate-400" />
            </button>
            <div className="p-6 bg-slate-50 text-slate-600 text-sm border-t border-slate-200 font-medium leading-relaxed">
              {item.answer}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const TextImageSection: React.FC<{ section: WebsiteSection }> = ({ section }) => {
  const isRight = section.styles?.imagePosition === 'right';
  return (
    <section className="py-32 bg-white overflow-hidden">
      <div className="max-w-[1600px] mx-auto px-6 flex flex-col lg:flex-row items-center gap-24">
        <div className={`w-full lg:w-1/2 space-y-8 ${isRight ? 'order-1' : 'order-2'}`}>
          <h2 className="text-5xl font-black text-slate-900 uppercase tracking-tighter leading-none">{section.title}</h2>
          <p className="text-xl text-slate-500 font-medium leading-relaxed">{section.subtitle}</p>
          {section.content?.bulletPoints && (
            <ul className="space-y-6 pt-4">
              {section.content.bulletPoints.map((b: string, i: number) => (
                <li key={i} className="flex items-center gap-4 text-sm font-bold uppercase tracking-wide text-slate-700">
                  <div className="w-8 h-8 rounded-full bg-red-50 text-[#e51b23] flex items-center justify-center shrink-0">
                    <CheckCircle2 size={16} />
                  </div>
                  {b}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className={`w-full lg:w-1/2 ${isRight ? 'order-2' : 'order-1'}`}>
          <div className="relative">
            <div className="absolute inset-0 bg-slate-900 rounded-[2.5rem] rotate-3 opacity-10"></div>
            <div className="bg-white p-2 rounded-[2.5rem] shadow-2xl border border-slate-100 relative z-10">
              <img src={section.content?.imageUrl} className={`w-full h-auto rounded-[2rem] object-cover`} style={{ objectPosition: section.content?.imageUrl?.includes('Mask-group-1') ? '100% 50%' : section.content?.imageUrl?.includes('Team%20(5)') ? '50% 15%' : section.content?.imageUrl?.includes('Team') ? '50% 50%' : '50% 50%' }} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const GallerySection: React.FC<{ section: WebsiteSection }> = ({ section }) => {
  const paddingClass = section.styles?.padding === 'small' ? 'py-12' : section.styles?.padding === 'large' ? 'py-48' : 'py-24';
  const alignClass = section.styles?.align === 'center' ? 'text-center mx-auto' : section.styles?.align === 'right' ? 'text-right ml-auto' : 'text-left mr-auto';

  return (
    <section className={`${paddingClass}`} style={{ backgroundColor: section.styles?.backgroundColor || '#ffffff' }}>
      <div className="max-w-[1600px] mx-auto px-6">
        <div className={`mb-16 max-w-3xl ${alignClass}`}>
          {section.title && <h2 className="text-4xl font-black uppercase tracking-tight mb-4" style={{ color: section.styles?.textColor || '#0f172a' }}>{section.title}</h2>}
          {section.subtitle && <p className="text-lg font-medium opacity-80" style={{ color: section.styles?.textColor || '#64748b' }}>{section.subtitle}</p>}
        </div>
        
        {section.content?.items && section.content.items.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {section.content.items.map((item: any, i: number) => (
              item.imageUrl && (
                <div key={i} className="rounded-2xl overflow-hidden shadow-lg aspect-[4/3] group relative">
                  <img src={item.imageUrl} className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-105`} style={{ objectPosition: item.imageUrl.includes('Mask-group-1') ? '100% 50%' : item.imageUrl.includes('Team%20(5)') ? '50% 15%' : item.imageUrl.includes('Team') ? '50% 50%' : '50% 50%' }} alt="Gallery item" />
                </div>
              )
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const StatsSection: React.FC<{ section: WebsiteSection }> = ({ section }) => {
  const paddingClass = section.styles?.padding === 'small' ? 'py-12' : section.styles?.padding === 'large' ? 'py-48' : 'py-24';
  const alignClass = section.styles?.align === 'center' ? 'text-center justify-center' : section.styles?.align === 'right' ? 'text-right justify-end' : 'text-left justify-start';

  return (
    <section className={`${paddingClass} border-y border-slate-100`} style={{ backgroundColor: section.styles?.backgroundColor || '#f8fafc' }}>
      <div className="max-w-7xl mx-auto px-6">
        <div className={`flex flex-col md:flex-row gap-12 md:gap-8 flex-wrap ${alignClass}`}>
          {section.content?.items?.map((item: any, i: number) => (
            <div key={i} className="flex-1 min-w-[200px]">
              <div className="text-5xl lg:text-7xl font-black tracking-tighter" style={{ color: section.styles?.textColor || '#0f172a' }}>
                {item.value}
              </div>
              <div className="text-sm font-bold uppercase tracking-widest mt-2" style={{ color: section.styles?.textColor ? `${section.styles.textColor}99` : '#64748b' }}>
                {item.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const VideoSection: React.FC<{ section: WebsiteSection }> = ({ section }) => {
  const paddingClass = section.styles?.padding === 'small' ? 'py-12' : section.styles?.padding === 'large' ? 'py-48' : 'py-32';
  const alignClass = section.styles?.align === 'center' ? 'text-center mx-auto' : section.styles?.align === 'right' ? 'text-right ml-auto' : 'text-left mr-auto';

  // Basic embed transformer
  let embedUrl = section.content?.videoUrl;
  if (embedUrl) {
    if (embedUrl.includes('youtube.com/watch?v=')) {
      embedUrl = embedUrl.replace('watch?v=', 'embed/');
    } else if (embedUrl.includes('youtu.be/')) {
      embedUrl = embedUrl.replace('youtu.be/', 'youtube.com/embed/');
    } else if (embedUrl.includes('vimeo.com/')) {
      embedUrl = embedUrl.replace('vimeo.com/', 'player.vimeo.com/video/');
    }
  }

  return (
    <section className={`${paddingClass}`} style={{ backgroundColor: section.styles?.backgroundColor || '#ffffff' }}>
      <div className="max-w-5xl mx-auto px-6">
        <div className={`mb-16 max-w-3xl ${alignClass}`}>
          {section.title && <h2 className="text-4xl font-black uppercase tracking-tight mb-4" style={{ color: section.styles?.textColor || '#0f172a' }}>{section.title}</h2>}
          {section.subtitle && <p className="text-lg font-medium opacity-80" style={{ color: section.styles?.textColor || '#64748b' }}>{section.subtitle}</p>}
        </div>
        
        {embedUrl ? (
          <div className="relative w-full aspect-video rounded-3xl overflow-hidden shadow-2xl border border-slate-200">
            <iframe
              src={embedUrl}
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        ) : (
          <div className="w-full aspect-video bg-slate-100 rounded-[2.5rem] flex items-center justify-center border-2 border-dashed border-slate-300">
            <div className="text-center text-slate-400">
              <Play size={48} className="mx-auto mb-4 opacity-50" />
              <p className="font-bold text-sm uppercase tracking-widest">Voeg een video URL toe in de editor</p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const TextOnlySection: React.FC<{ section: WebsiteSection }> = ({ section }) => {
  const paddingClass = section.styles?.padding === 'small' ? 'py-12' : section.styles?.padding === 'large' ? 'py-48' : 'py-32';
  const alignClass = section.styles?.align === 'center' ? 'text-center items-center mx-auto' : section.styles?.align === 'right' ? 'text-right items-end ml-auto' : 'text-left items-start mr-auto';

  return (
    <section className={`${paddingClass}`} style={{ backgroundColor: section.styles?.backgroundColor || '#ffffff' }}>
      <div className="max-w-4xl mx-auto px-6">
        <div className={`flex flex-col gap-8 ${alignClass}`}>
          {section.title && (
            <h2 className="text-4xl lg:text-6xl font-black uppercase tracking-tighter" style={{ color: section.styles?.textColor || '#0f172a' }}>
              {section.title}
            </h2>
          )}
          {section.subtitle && (
            <div className="text-xl font-medium leading-relaxed opacity-90 whitespace-pre-wrap max-w-3xl" style={{ color: section.styles?.textColor || '#475569' }}>
              {section.subtitle}
            </div>
          )}
          {section.content?.ctaLabel && (
            <button className="px-8 py-4 rounded-xl font-black text-sm uppercase tracking-widest transition-all shadow-lg hover:-translate-y-1" style={{ backgroundColor: section.styles?.buttonColor || '#e51b23', color: section.styles?.buttonTextColor || '#ffffff' }}>
              {section.content.ctaLabel}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

const TeamSection: React.FC<{ section: WebsiteSection }> = ({ section }) => {
  const paddingClass = section.styles?.padding === 'small' ? 'py-12' : section.styles?.padding === 'large' ? 'py-48' : 'py-24';
  const alignClass = section.styles?.align === 'center' ? 'text-center mx-auto' : section.styles?.align === 'right' ? 'text-right ml-auto' : 'text-left mr-auto';
  const radiusClass = section.styles?.borderRadius === 'sharp' ? 'rounded-none' : section.styles?.borderRadius === 'playful' ? 'rounded-[3rem]' : 'rounded-3xl';
  const shadowClass = section.styles?.shadow === 'none' ? 'shadow-none' : section.styles?.shadow === 'prominent' ? 'shadow-2xl' : 'shadow-lg';

  return (
    <section className={`${paddingClass}`} style={{ backgroundColor: section.styles?.backgroundColor || '#f8fafc' }}>
      <div className="max-w-7xl mx-auto px-6">
        <div className={`mb-16 max-w-3xl ${alignClass}`}>
          {section.title && <h2 className="text-4xl font-black uppercase tracking-tight mb-4" style={{ color: section.styles?.textColor || '#0f172a' }}>{section.title}</h2>}
          {section.subtitle && <p className="text-lg font-medium opacity-80" style={{ color: section.styles?.textColor || '#64748b' }}>{section.subtitle}</p>}
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {section.content?.items?.map((member: any, i: number) => (
            <div key={i} className={`bg-white border border-slate-100 overflow-hidden ${radiusClass} ${shadowClass} group`}>
              <div className="aspect-square bg-slate-100 relative overflow-hidden flex items-center justify-center">
                {member.imageUrl ? (
                  <img src={member.imageUrl} className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500`} style={{ objectPosition: member.imageUrl.includes('Mask-group-1') ? '100% 50%' : member.imageUrl.includes('Team%20(5)') ? '50% 15%' : member.imageUrl.includes('Team') ? '50% 50%' : '50% 50%' }} alt={member.name} />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-300"><Users size={48} /></div>
                )}
              </div>
              <div className="p-6 text-center">
                <h3 className="font-black text-lg text-slate-900 uppercase tracking-tight mb-1">{member.name || 'Naam'}</h3>
                <p className="text-sm font-bold text-[#e51b23] uppercase tracking-widest">{member.role || 'Rol'}</p>
                {member.description && <p className="mt-4 text-sm font-medium text-slate-500 line-clamp-3">{member.description}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const PricingSection: React.FC<{ section: WebsiteSection, onLoginClick?: () => void }> = ({ section, onLoginClick }) => {
  const paddingClass = section.styles?.padding === 'small' ? 'py-12' : section.styles?.padding === 'large' ? 'py-48' : 'py-24';
  const radiusClass = section.styles?.borderRadius === 'sharp' ? 'rounded-none' : section.styles?.borderRadius === 'playful' ? 'rounded-[3rem]' : 'rounded-3xl';
  const shadowClass = section.styles?.shadow === 'none' ? 'shadow-none' : section.styles?.shadow === 'prominent' ? 'shadow-2xl' : 'shadow-xl';

  return (
    <section className={`${paddingClass}`} style={{ backgroundColor: section.styles?.backgroundColor || '#ffffff' }}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-16">
          {section.title && <h2 className="text-4xl font-black uppercase tracking-tight mb-4" style={{ color: section.styles?.textColor || '#0f172a' }}>{section.title}</h2>}
          {section.subtitle && <p className="text-lg font-medium opacity-80" style={{ color: section.styles?.textColor || '#64748b' }}>{section.subtitle}</p>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {section.content?.items?.map((plan: any, i: number) => (
            <div key={i} className={`bg-white p-8 border border-slate-200 flex flex-col relative overflow-hidden group hover:-translate-y-2 transition-transform duration-300 ${radiusClass} ${shadowClass}`}>
              {plan.highlighted && (
                <div className="absolute top-0 inset-x-0 h-1.5" style={{ backgroundColor: section.styles?.buttonColor || '#e51b23' }}></div>
              )}
              <h3 className="text-xl font-black uppercase tracking-tight mb-2 text-slate-900">{plan.name || 'Plan Naam'}</h3>
              <p className="text-4xl font-black text-slate-900 mb-8 tracking-tight">{plan.price || '€0'}</p>
              
              <ul className="space-y-4 mb-8 flex-1">
                {plan.features?.split(',').map((f: string, j: number) => (
                  <li key={j} className="flex items-start gap-3 text-sm font-bold text-slate-600">
                    <CheckCircle2 size={16} className="mt-0.5 shrink-0" style={{ color: section.styles?.buttonColor || '#10b981' }} />
                    <span className="leading-snug">{f.trim()}</span>
                  </li>
                ))}
              </ul>
              
              <button 
                onClick={onLoginClick} 
                className="w-full py-4 rounded-xl font-black text-xs uppercase tracking-widest transition-all shadow-md hover:shadow-lg"
                style={{ 
                  backgroundColor: plan.highlighted ? (section.styles?.buttonColor || '#0f172a') : '#f1f5f9',
                  color: plan.highlighted ? (section.styles?.buttonTextColor || '#ffffff') : '#0f172a'
                }}
              >
                {plan.cta || 'Kies dit pakket'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ContactSection: React.FC<{ section: WebsiteSection }> = ({ section }) => {
  const paddingClass = section.styles?.padding === 'small' ? 'py-12' : section.styles?.padding === 'large' ? 'py-48' : 'py-24';
  const radiusClass = section.styles?.borderRadius === 'sharp' ? 'rounded-none' : section.styles?.borderRadius === 'playful' ? 'rounded-[3rem]' : 'rounded-3xl';
  const shadowClass = section.styles?.shadow === 'none' ? 'shadow-none' : section.styles?.shadow === 'prominent' ? 'shadow-2xl' : 'shadow-lg';

  return (
    <section className={`${paddingClass}`} style={{ backgroundColor: section.styles?.backgroundColor || '#ffffff' }}>
      <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row gap-16">
        <div className="w-full md:w-1/2">
          {section.title && <h2 className="text-4xl font-black uppercase tracking-tight mb-6" style={{ color: section.styles?.textColor || '#0f172a' }}>{section.title}</h2>}
          {section.subtitle && <p className="text-lg font-medium leading-relaxed mb-8 opacity-80" style={{ color: section.styles?.textColor || '#64748b' }}>{section.subtitle}</p>}
          
          <div className="space-y-6">
            {section.content?.email && (
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-red-50 text-[#e51b23] rounded-2xl flex items-center justify-center shrink-0"><Mail size={20} /></div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Email</p>
                  <p className="font-bold text-slate-800">{section.content.email}</p>
                </div>
              </div>
            )}
            {section.content?.phone && (
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-red-50 text-[#e51b23] rounded-2xl flex items-center justify-center shrink-0"><Phone size={20} /></div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Telefoon</p>
                  <p className="font-bold text-slate-800">{section.content.phone}</p>
                </div>
              </div>
            )}
            {section.content?.address && (
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-50 text-[#e51b23] rounded-2xl flex items-center justify-center shrink-0"><MapPin size={20} /></div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Adres</p>
                  <p className="font-bold text-slate-800 whitespace-pre-wrap">{section.content.address}</p>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {section.content?.imageUrl && (
          <div className="w-full md:w-1/2">
            <div className={`w-full aspect-square md:aspect-auto md:h-full bg-slate-100 overflow-hidden relative ${radiusClass} ${shadowClass}`}>
              <img src={section.content.imageUrl} className="absolute inset-0 w-full h-full object-cover" alt="Locatie of Kantoor" />
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const FooterSection: React.FC<{ section: WebsiteSection }> = ({ section }) => {
  return (
    <footer className="py-16 md:py-24" style={{ backgroundColor: section.styles?.backgroundColor || '#0F172A', color: section.styles?.textColor || '#ffffff' }}>
      <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-8">
        <div className="text-center md:text-left">
          {section.title && (
            <div className="flex items-center gap-3 mb-4 justify-center md:justify-start">
              <span className="text-2xl font-black tracking-tight">{section.title}</span>
            </div>
          )}
          {section.subtitle && (
            <p className="font-medium text-xs uppercase tracking-widest max-w-sm opacity-60">
              {section.subtitle}
            </p>
          )}
        </div>
        
        {section.content?.ctaLabel && (
          <div className="flex gap-8">
            <button className="text-[10px] font-bold uppercase tracking-widest hover:opacity-80 transition-colors flex items-center gap-2">
              <Lock size={12} /> {section.content.ctaLabel}
            </button>
          </div>
        )}
      </div>
    </footer>
  );
};

const DYNAMIC_COMPONENTS: Record<string, React.FC<any>> = {
  hero: HeroSection,
  features: FeatureSection,
  logos: LogosSection,
  testimonials: TestimonialsSection,
  faq: FAQSection,
  text_image: TextImageSection,
  gallery: GallerySection,
  stats: StatsSection,
  video: VideoSection,
  text_only: TextOnlySection,
  team: TeamSection,
  pricing: PricingSection,
  contact: ContactSection,
  footer: FooterSection
};

export default LandingPage;
